import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { BehaviorSubject, Observable, of } from 'rxjs'
import { finalize, shareReplay, tap } from 'rxjs/operators'

import {
  ApiKey,
  CreatedApiKey,
  FavoritesList,
  MinimalProfile,
  Profile,
  UpdatePassword,
} from '@cyborg-monorepo/hunter/common'

import { VerityEnvironmentService } from '../../../../environments/verity-environment.service'

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private httpClient = inject(HttpClient)
  private verityEnvironmentService = inject(VerityEnvironmentService)

  // Single source of truth for profile state
  private profileSubject = new BehaviorSubject<Profile | null>(null)
  public readonly profile$ = this.profileSubject.asObservable()

  // In-flight request cache (prevents duplicate HTTP calls)
  private profileRequest$?: Observable<Profile>

  // =========================
  // Profile loading & caching
  // =========================

  /**
   * Loads the user profile once and caches it.
   * - Multiple callers share one HTTP request
   * - Cached value is reused
   * - `force = true` re-fetches from server
   */
  public loadProfile(force = false): Observable<Profile> {
    if (!force) {
      const cached = this.profileSubject.value
      if (cached) {
        return of(cached)
      }

      if (this.profileRequest$) {
        return this.profileRequest$
      }
    }

    // Clear stale profile immediately on force-refresh so that concurrent callers
    // using loadProfile(false) join the in-flight request instead of reading
    // the old cached value (a different user's profile).
    if (force) {
      this.profileSubject.next(null)
    }

    this.profileRequest$ = this.httpClient
      .get<Profile>(`${this.verityEnvironmentService.apiUrl}/me/profile`)
      .pipe(
        tap((profile) => this.profileSubject.next(profile)),
        finalize(() => {
          this.profileRequest$ = undefined
        }),
        shareReplay(1),
      )

    return this.profileRequest$
  }

  /**
   * Updates the profile and refreshes from server
   * Use after profile-modifying operations
   */
  public refreshProfile(): Observable<Profile> {
    return this.loadProfile(true)
  }

  /**
   * Manually set profile (useful for optimistic updates)
   */
  public setProfile(profile: Profile): void {
    this.profileSubject.next(profile)
  }

  /**
   * Clears cached profile (e.g. on logout)
   */
  public clearProfile(): void {
    this.profileSubject.next(null)
    this.profileRequest$ = undefined
  }

  /**
   * Synchronous access (use sparingly)
   */
  public getCurrentProfile(): Profile | null {
    return this.profileSubject.value
  }

  // =========================
  // Profile-dependent APIs
  // =========================

  public getFavorites(size: number, page: number): Observable<FavoritesList> {
    return this.httpClient.get<FavoritesList>(
      `${this.verityEnvironmentService.apiUrl}/me/favorites?size=${size}&page=${page}`,
    )
  }

  public getApiKeys(): Observable<{ keys: Array<ApiKey> }> {
    return this.httpClient.get<{ keys: Array<ApiKey> }>(
      `${this.verityEnvironmentService.apiUrl}/me/api-keys`,
    )
  }

  public createApiKey(name: string): Observable<CreatedApiKey> {
    return this.httpClient.post<CreatedApiKey>(
      `${this.verityEnvironmentService.apiUrl}/me/api-keys`,
      { name },
    )
  }

  public deleteApiKey(keyId: string): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.verityEnvironmentService.apiUrl}/me/api-keys/${keyId}`,
    )
  }

  public updatePassword(updatePassword: UpdatePassword): Observable<void> {
    return this.httpClient.put<void>(
      `${this.verityEnvironmentService.apiUrl}/me/update-password`,
      updatePassword,
    )
  }

  // =========================
  // Company / user management
  // =========================

  public createUser(user: MinimalProfile): Observable<Profile> {
    return this.httpClient.post<Profile>(
      `${this.verityEnvironmentService.apiUrl}/company/users/new`,
      user,
    )
  }

  public updateUser(id: number, user: MinimalProfile): Observable<void> {
    return this.httpClient.put<void>(
      `${this.verityEnvironmentService.apiUrl}/company/users/${id}`,
      user,
    )
  }

  public deleteUser(id: number): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.verityEnvironmentService.apiUrl}/company/users/${id}`,
    )
  }

  public getUsers(): Observable<Profile[]> {
    return this.httpClient.get<Profile[]>(
      `${this.verityEnvironmentService.apiUrl}/company/users`,
    )
  }
}
